import { useLocation } from 'wouter'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { skipToken, useSuspenseQuery } from '@apollo/client/react'
import { Trans, useLingui } from '@lingui/react/macro'

import {
  Delete01Icon,
  FloppyDiskIcon,
  PlusSignIcon
} from '@hugeicons/core-solid-rounded'

import { useCloseModal } from '@/Router'
import * as Form from '@/features/form'
import { useBranding } from '@/features/branding'
import Button from '@/components/ui/Button'
import { Permission, useCheckPermission } from '@/features/auth'
import { PromotionsByGoodie } from '@/features/promotion'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import * as Tabs from '@/components/ui/Tabs'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { Suspense } from '@/components/ui/Suspense'
import { useTypeToLabel } from '../utils/helpers'

import useGoodieForm, {
  FormData,
  TRANSLATABLE_FIELDS
} from '../hooks/useGoodieForm'

// Fields
import MessageFields from './fields/MessageFields'
import DefaultFields from './fields/DefaultFields'
import EventFields from './fields/EventFields'
import { generateDefaults } from '../utils/generateFormDefaults'
import { CommonFields } from './fields/CommonFields'

interface Props {
  id?: number
  type?: GraphQL.GoodieType
}

export default function GoodieForm(props: Props) {
  const { type, id } = props

  const { data: goodieData } = useSuspenseQuery(
    AdminAPI.goodie.GET,
    id
      ? {
          variables: { id, withGallery: true, withTranslations: true }
        }
      : skipToken
  )

  const canCreateAndEditGoodie = useCheckPermission([
    Permission.GOODIE_CREATE,
    Permission.GOODIE_UPDATE
  ])

  const goodie = getFragment(
    SharedAPI.goodie.GOODIE_FRAGMENT,
    goodieData?.getGoodie
  )

  const finalType = useMemo(
    () => goodie?.type ?? type ?? GraphQL.GoodieType.Default,
    [goodie, type]
  )

  return (
    <FormContent
      goodie={goodie}
      readOnly={!canCreateAndEditGoodie}
      currentType={finalType}
    />
  )
}

interface ContentProps {
  goodie?: GraphQL.GoodieFragmentFragment
  promotions?: GraphQL.PromotionFragmentFragment[] | null
  currentType: GraphQL.GoodieType
  readOnly: boolean
}

function FormContent({ goodie, currentType, readOnly }: ContentProps) {
  const { t } = useLingui()
  const [, navigate] = useLocation()
  const closeModal = useCloseModal()
  const formRef = useRef<Form.RefType<FormData>>(null)
  const shouldCreateAnotherGoodie = useRef(false)
  const submitRef = useRef<HTMLButtonElement>(null)

  const goodieTypeToLabel = useTypeToLabel()

  const shouldShowPromotionTab = readOnly || !!goodie

  const {
    branding: { supportedLocales }
  } = useBranding()

  const defaultValues = useRef(
    generateDefaults({
      type: currentType,
      goodie,
      supportedLocales: supportedLocales ?? []
    })
  )

  const resetToDefault = useCallback(
    ({ keepDirtyValues = false }: { keepDirtyValues?: boolean } = {}) => {
      if (!formRef.current) return

      formRef.current.reset(defaultValues.current, {
        keepDirtyValues
      })

      switch (defaultValues.current.type) {
        case GraphQL.GoodieType.Default:
          formRef.current.resetField('event')
          formRef.current.resetField('message')
          break
        case GraphQL.GoodieType.Message:
          formRef.current.resetField('default')
          formRef.current.resetField('event')
          break
        case GraphQL.GoodieType.Event:
          formRef.current.resetField('default')
          formRef.current.resetField('message')
          break
      }
    },
    []
  )

  useEffect(() => {
    if (!formRef.current) return

    defaultValues.current = generateDefaults({
      type: currentType,
      goodie,
      supportedLocales: supportedLocales ?? []
    })

    resetToDefault({ keepDirtyValues: true })

    // For messages we remove all files but 1
    if (currentType === GraphQL.GoodieType.Message) {
      const values = formRef.current.getValues()

      formRef.current.setValue('files', values.files.slice(0, 1))
    }
  }, [goodie, currentType, resetToDefault, supportedLocales])

  const onDone = useCallback(
    (newGoodie?: GraphQL.GoodieFragmentFragment, deleted?: boolean) => {
      if (deleted) {
        closeModal()

        return
      }

      if (goodie) return
      if (!newGoodie) return

      // This needs to run in the submitted successfully hook, so that use-react-form is ready
      if (shouldCreateAnotherGoodie.current) return

      navigate(`~/app/goodie/${newGoodie.token}?goodieTab=upsell`)
    },
    [goodie, navigate, closeModal]
  )

  const onSubmittedSuccessfully = useCallback(() => {
    if (!shouldCreateAnotherGoodie.current) return

    shouldCreateAnotherGoodie.current = false
    resetToDefault()
  }, [resetToDefault])

  const { errors, create, update, remove, progress } = useGoodieForm({
    id: goodie?.id,
    onDone
  })

  const createAndNew = useCallback(() => {
    shouldCreateAnotherGoodie.current = true
    submitRef.current?.click()
  }, [])

  const goodieType = goodieTypeToLabel[currentType]
  const name = goodie?.name

  const tabs = useMemo(() => {
    const common = [
      { id: 'info', text: t`General` },
      { id: 'specific', text: t`${goodieType} specific` }
    ]

    if (shouldShowPromotionTab) {
      common.push({ id: 'upsell', text: t`Upselling Options` })
    }

    return common
  }, [goodieType, shouldShowPromotionTab, t])

  return (
    <Form.Root<FormData>
      onSubmit={goodie ? update : create}
      serverErrors={errors}
      readOnly={readOnly}
      defaultValues={defaultValues.current}
      ref={formRef}
      onSubmittedSuccessfully={onSubmittedSuccessfully}
      translatableFields={TRANSLATABLE_FIELDS}
      generateDefaultValues={() =>
        generateDefaults({
          type: currentType,
          goodie,
          supportedLocales: supportedLocales ?? []
        })
      }
    >
      <Tabs.RootAsQueryParam
        headings={tabs}
        queryParamName="goodieTab"
        areSteps={!goodie}
      >
        <Tabs.Tab id="info" keepMounted>
          <CommonFields />
          <CommonButtons
            name={name}
            createAndNew={createAndNew}
            remove={remove}
            goodie={goodie}
            progress={progress}
            submitRef={submitRef}
            readOnly={readOnly}
          />
        </Tabs.Tab>
        <Tabs.Tab id="specific" keepMounted>
          <div className="relative flex w-full flex-col gap-6 pt-4">
            <Suspense>
              <Form.SpecificData<GraphQL.GoodieType> name="type">
                {type => {
                  switch (type) {
                    case GraphQL.GoodieType.Default:
                      return <DefaultFields />
                    case GraphQL.GoodieType.Event:
                      return <EventFields />
                    case GraphQL.GoodieType.Message:
                      return <MessageFields />
                  }
                }}
              </Form.SpecificData>
            </Suspense>
          </div>
          <CommonButtons
            name={name}
            createAndNew={createAndNew}
            remove={remove}
            goodie={goodie}
            progress={progress}
            submitRef={submitRef}
            readOnly={readOnly}
          />
        </Tabs.Tab>
        {shouldShowPromotionTab && goodie && (
          <Tabs.Tab id="upsell" keepMounted>
            <div className="relative flex w-full flex-col gap-6 pt-4">
              <Suspense>
                <PromotionsByGoodie id={goodie.id} token={goodie.token} />
              </Suspense>
            </div>
          </Tabs.Tab>
        )}
      </Tabs.RootAsQueryParam>
    </Form.Root>
  )
}

function CommonButtons({
  name,
  progress,
  goodie,
  readOnly,
  submitRef,
  remove,
  createAndNew
}: {
  name?: string
  progress?: number | null
  goodie?: GraphQL.GoodieFragmentFragment
  submitRef: React.RefObject<HTMLButtonElement | null>
  readOnly: boolean
  remove: () => void
  createAndNew: () => void
}) {
  const trigger = Form.useTrigger()

  const { t } = useLingui()
  const isNew = !goodie
  const { tab, setTab } = useContext(Tabs.TabsContext) ?? {}

  const handleNextStep = useCallback(async () => {
    // Validate only specific fields from info part
    const isValid = await trigger(['name', 'title'])

    if (isValid) setTab?.('specific')
  }, [trigger, setTab])

  if (readOnly) return null

  const showNext = isNew && tab === 'info'
  const showBack = isNew && tab === 'specific'
  const showSave = (isNew && tab === 'specific') || !isNew
  const showSaveAndNew = isNew && tab === 'specific'

  return (
    <div className="flex flex-col">
      {progress != null && (
        <ProgressBar value={progress} title={t`Upload Files`} />
      )}
      <footer className="flex flex-row items-center justify-between gap-2.5 mt-2.5">
        <div className="flex flex-row justify-start gap-2.5">
          {goodie && (
            <ConfirmDialog
              title={t`Delete Goodie?`}
              message={t`Are you sure that you want to delete the Goodie "${name}", and all it's promotions, this can not be reversed?`}
              onConfirm={remove}
              asChild
            >
              <Button type="button" variant="outline" Icon={Delete01Icon}>
                <Trans>Delete</Trans>
              </Button>
            </ConfirmDialog>
          )}
          {showBack && (
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTab?.('info')}
              >
                <Trans>Back</Trans>
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-row items-center justify-end gap-2.5">
          {showSaveAndNew && (
            <div>
              <Form.Submit
                type="button"
                variant="outline"
                Icon={PlusSignIcon}
                onClick={createAndNew}
              >
                <Trans>Save and New</Trans>
              </Form.Submit>
            </div>
          )}
          {showNext && (
            <div>
              <Button type="button" onClick={handleNextStep}>
                <Trans>Next</Trans>
              </Button>
            </div>
          )}
          {showSave && (
            <div>
              <Form.Submit
                Icon={FloppyDiskIcon}
                ref={submitRef}
                onlyValidWhenDirty={!!goodie}
              >
                <Trans>Save</Trans>
              </Form.Submit>
            </div>
          )}
        </div>
      </footer>
    </div>
  )
}
